import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { api, errText } from "@/lib/api";
import { clearHandoff, pendingHandoff } from "@/lib/googleAuth";
import { Logo } from "@/components/Brand";
import { isStandalone } from "@/lib/install";
import Shell from "@/components/Shell";
import AuthPage from "@/pages/AuthPage";
import CheckIn from "@/pages/CheckIn";
import Emergency from "@/pages/Emergency";
import Awareness from "@/pages/Awareness";
import Grounding from "@/pages/Grounding";
import Reflection from "@/pages/Reflection";
import ReachOut from "@/pages/ReachOut";
import History from "@/pages/History";
import Entry from "@/pages/Entry";
import FlipTool from "@/pages/FlipTool";
import Cards from "@/pages/Cards";
import Schemas from "@/pages/Schemas";
import Affirmations from "@/pages/Affirmations";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import MagicLink from "@/pages/MagicLink";
import SharedReport from "@/pages/SharedReport";

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } } });

function Splash({ label }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="splash">
      <div className="animate-pulse"><Logo size={64} /></div>
      {label ? <p className="text-t-muted text-sm" data-testid="splash-label">{label}</p> : null}
    </div>
  );
}

function AuthCallback() {
  const { signIn, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    const handoff = isStandalone() ? null : pendingHandoff();
    api
      .post("/auth/google/session", {}, { headers: { "X-Session-ID": sid, ...(handoff ? { "X-Handoff": handoff } : {}) }, timeout: 25000 })
      .then(({ data }) => {
        signIn(data);
        if (!handoff) clearHandoff();
        if (handoff) toast.success("Signed in. If you started from the home-screen app, close this window to carry on there.", { duration: 9000 });
        navigate("/", { replace: true });
      })
      .catch((e) => {
        toast.error(errText(e));
        navigate("/auth", { replace: true });
        refresh();
      });
  }, [location.hash, location.search, navigate, signIn, refresh]);
  return <Splash label="Signing you in…" />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/auth" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/magic" element={<MagicLink />} />
      <Route path="/report/:token" element={<SharedReport />} />
      <Route path="/auth" element={user ? <Navigate to="/" replace /> : user === null ? <Splash /> : <AuthPage />} />
      <Route element={<Protected><Shell /></Protected>}>
        <Route path="/" element={<CheckIn />} />
        <Route path="/history" element={<History />} />
        <Route path="/affirmations" element={<Affirmations />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="/emergency/:id" element={<Protected><Emergency /></Protected>} />
      <Route path="/awareness/:id" element={<Protected><Awareness /></Protected>} />
      <Route path="/grounding" element={<Protected><Grounding /></Protected>} />
      <Route path="/reflection/:id" element={<Protected><Reflection /></Protected>} />
      <Route path="/reach-out" element={<Protected><ReachOut /></Protected>} />
      <Route path="/entry/:id" element={<Protected><Entry /></Protected>} />
      <Route path="/flip" element={<Protected><FlipTool /></Protected>} />
      <Route path="/cards" element={<Protected><Cards /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/schemas" element={<Protected><Schemas /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" offset={20} richColors toastOptions={{ style: { fontFamily: "Plus Jakarta Sans", borderRadius: 16 } }} />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
